import {
  getSafeSingletonDeployment,
  getProxyFactoryDeployment,
  getFallbackHandlerDeployment,
  getMultiSendDeployment,
  getMultiSendCallOnlyDeployment,
  getSignMessageLibDeployment,
  getCreateCallDeployment,
  getSimulateTxAccessorDeployment,
  getTokenCallbackHandlerDeployment,
} from '@safe-global/safe-deployments'

const localContractNetworks = {
  31337: {
    safeSingletonAddress: '0x41675C099F32341bf84BFc5382aF534df5C7461a',
    safeProxyFactoryAddress: '0x4e1DCf7AD4e460CfD30791CCC4F9c8a4f820ec67',
    multiSendAddress: '0x38869bf66a61cF6bDB996A6aE40D5853Fd43B526',
    multiSendCallOnlyAddress: '0x9641d764fc13c8B624c04430C7356C1C7C8102e2',
    fallbackHandlerAddress: '0xfd0732Dc9E303f09fCEf3a7388Ad10A83459Ec99',
    signMessageLibAddress: '0xd53cd0aB83D845Ac265BE939c57F53AD838012c9',
    createCallAddress: '0x9b35Af71d77eaf8d7e40252370304687390A1A52',
    simulateTxAccessorAddress: '0x3d4BA2E0884aa488718476ca2FB8Efc291A46199',
    tokenCallbackHandlerAddress: '0xeDCF620325E82e3B9836eaaeFdc4283E99Dd7562',
    safeToL2SetupAddress: '0xBD89A1CE4DDe368FFAB0eC35506eEcE0b1Ffdc54',
    safeL2Address: '0x29fcB43b46531BcA003ddC8FCB67FFE91900C762',
    safeToL2MigrationAddress: '0xfF83F6335d8930cBad1c0D439A841f01888D9f69',
    safeMigrationAddress: '0x526643F69b81B008F46d95CD5ced5eC0edFFDaC6',
  },
}

export type ContractAddresses = {
  safeSingletonAddress?: string
  safeProxyFactoryAddress?: string
  fallbackHandlerAddress?: string
  multiSendAddress?: string
  multiSendCallOnlyAddress?: string
  signMessageLibAddress?: string
  createCallAddress?: string
  simulateTxAccessorAddress?: string
  tokenCallbackHandlerAddress?: string
}

export type ContractNetworks = {
  [chainId: string]: ContractAddresses
}

export async function buildContractNetworks(
  chainIds: number[],
  safeVersion = '1.4.1'
): Promise<ContractNetworks> {
  const contractNetworks: ContractNetworks = {}
  for (const chainId of chainIds) {
    if (chainId === 31337) {
      contractNetworks[chainId] = localContractNetworks[31337]
      continue
    }
    try {
      const singleton = getSafeSingletonDeployment({
        network: chainId.toString(),
        version: safeVersion,
      })
      const proxyFactory = getProxyFactoryDeployment({
        network: chainId.toString(),
        version: safeVersion,
      })
      const fallbackHandler = getFallbackHandlerDeployment({
        network: chainId.toString(),
        version: safeVersion,
      })
      const multiSend = getMultiSendDeployment({ network: chainId.toString() })
      const multiSendCallOnly = getMultiSendCallOnlyDeployment({
        network: chainId.toString(),
      })
      const signMessageLib = getSignMessageLibDeployment({
        network: chainId.toString(),
      })
      const createCall = getCreateCallDeployment({
        network: chainId.toString(),
      })
      const simulateTxAccessor = getSimulateTxAccessorDeployment({
        network: chainId.toString(),
      })
      const tokenCallbackHandler = getTokenCallbackHandlerDeployment({
        network: chainId.toString(),
      })

      contractNetworks[chainId] = {
        safeSingletonAddress: singleton?.defaultAddress,
        safeProxyFactoryAddress: proxyFactory?.defaultAddress,
        fallbackHandlerAddress: fallbackHandler?.defaultAddress,
        multiSendAddress: multiSend?.defaultAddress,
        multiSendCallOnlyAddress: multiSendCallOnly?.defaultAddress,
        signMessageLibAddress: signMessageLib?.defaultAddress,
        createCallAddress: createCall?.defaultAddress,
        simulateTxAccessorAddress: simulateTxAccessor?.defaultAddress,
        tokenCallbackHandlerAddress: tokenCallbackHandler?.defaultAddress,
      }
    } catch {
      console.error(
        `Safe contracts for version 1.4.1 not found on chain ${chainId}. Skipping.`
      )
      continue
    }
  }
  return contractNetworks
}
